import clsx from 'clsx';
import { CSSProperties, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { noopFunction } from '@vestico/utils';

import { WidgetGridLayoutConfiguration, WidgetItemAspectRatioDefault } from '@vestico/model';

import { GlobalsContext, useElementSize } from '@vestico/shared';

import { CarouselItem } from '../common/CarouselItem';
import { useCarouselStyles } from '../hooks/useCarouselStyles';
import { useInViewVideo } from '../hooks/useInViewVideo';
import { usePreviewTexts } from '../hooks/usePreviewTexts';
import { BaseLayoutProps } from '../model/baseLayoutProps';
import { getResponseElementID } from '../model/layoutHelpers';
import { EmbedStyleButton } from './EmbedStyleButton';
import style from './GridLayout.module.css';
import { LayoutLoadMoreHandler } from './LayoutLoadMoreHandler';
import { getItemsToShow } from './RowLayout';

export interface GridLayoutProps extends BaseLayoutProps {
  layout: WidgetGridLayoutConfiguration;
}

export const GridLayout = ({ layout, responses, onResponseClick, hasMore: hasMoreRemote, loadMore, loading }: GridLayoutProps) => {
  const { numberOfRows, visibleItems, loadMoreCSSStyle, loadMoreButtonType, showLikes, itemBorderRadius } = layout;
  const aspectRatio = useMemo(() => layout.aspectRatio ?? WidgetItemAspectRatioDefault, [layout.aspectRatio]);
  const itemPadding = useMemo(() => (layout.itemPadding !== undefined ? layout.itemPadding : 6), [layout.itemPadding]);

  const [pageIndex, setPageIndex] = useState(0);
  const mainContainerRef = useRef<HTMLDivElement>(null);

  const { useMobileLayout } = useContext(GlobalsContext);
  const [itemSize, setItemSize] = useState<number | undefined>(undefined);
  const [itemsPerRow, setItemsPerRow] = useState<number>(2);
  const { t: sharedTrans } = useTranslation('shared');

  const mainContainerSize = useElementSize(mainContainerRef);

  useEffect(() => {
    const calculateItemsToShow = () => {
      const mainContainerWidth = mainContainerSize.width;
      const newItemsPerRow = getItemsToShow(visibleItems, mainContainerWidth, responses.length);

      const numberOfPaddings = newItemsPerRow - 1;
      const itemWidth = Math.min((mainContainerWidth - numberOfPaddings * (itemPadding ?? 0)) / newItemsPerRow, 720);
      setItemsPerRow(newItemsPerRow);
      setItemSize(itemWidth);
    };

    calculateItemsToShow();
  }, [useMobileLayout, mainContainerSize, responses, itemPadding, visibleItems]);

  const calculateMaxIndex = useCallback(
    (thePageIndex: number) => (thePageIndex + 1) * (itemsPerRow * (numberOfRows ?? 1)) - 1,
    [itemsPerRow, numberOfRows]
  );
  const maxIndex = useMemo(() => calculateMaxIndex(pageIndex), [calculateMaxIndex, pageIndex]);

  const responsePreviewTexts = usePreviewTexts(layout, responses);

  const handleResponseClick = useCallback((index: number) => onResponseClick(index), [onResponseClick]);
  const { firstInViewVideoResponse, updateInView } = useInViewVideo({ layout, responses });
  const onUpdateInView = useCallback((index: number) => (inView: boolean) => updateInView(index, inView), [updateInView]);

  const { styles, borderRadiusStyles } = useCarouselStyles({ itemSize, borderRadius: itemBorderRadius, aspectRatio });

  const responseMarkups = useMemo(() => {
    if (responses.length === 0) {
      return [];
    }

    const children = responses.slice(0, maxIndex + 1).map((r, index) => {
      return (
        <div id={getResponseElementID(r)} key={r.postDocumentID}>
          <CarouselItem
            response={r}
            index={index}
            onClick={handleResponseClick}
            previewText={responsePreviewTexts[index]}
            showLikes={showLikes}
            aspectRatio={aspectRatio}
            updateInView={onUpdateInView(index)}
            playVideo={r === firstInViewVideoResponse}
            borderRadius={borderRadiusStyles}
          />
        </div>
      );
    });

    return children;
  }, [
    responses,
    maxIndex,
    handleResponseClick,
    responsePreviewTexts,
    showLikes,
    aspectRatio,
    onUpdateInView,
    firstInViewVideoResponse,
    borderRadiusStyles,
  ]);

  const hasMore = useMemo(() => responses.length > maxIndex || hasMoreRemote, [hasMoreRemote, maxIndex, responses.length]);

  const onLoadMore = useCallback(() => {
    if (!hasMore) {
      return;
    }

    const nextPageMaxIndex = calculateMaxIndex(pageIndex + 1);
    const hasMoreLocal = responses.length > nextPageMaxIndex;

    if (!hasMoreLocal && loadMore) {
      loadMore();
    }
    setPageIndex(pageIndex + 1);
  }, [calculateMaxIndex, hasMore, loadMore, pageIndex, responses.length]);

  const bottomContent = useMemo(() => {
    if (loading) {
      return <LayoutLoadMoreHandler loading={loading} loadMore={noopFunction} />;
    }
    if (!hasMore) {
      return null;
    }
    return (
      <div className={clsx(style.loadMoreContainer)}>
        <EmbedStyleButton
          content={sharedTrans('LOC_LOAD_MORE')}
          onClick={onLoadMore}
          customCSS={loadMoreCSSStyle}
          type={loadMoreButtonType ?? 'anchor'}
        />
      </div>
    );
  }, [hasMore, loadMoreButtonType, loadMoreCSSStyle, loading, onLoadMore, sharedTrans]);

  return (
    <div className={clsx(style.root)} style={styles} ref={mainContainerRef}>
      <div
        className={clsx(style.grid)}
        style={{ '--grid-layout-item-padding': `${itemPadding}px`, '--grid-layout-items-per-row': `${itemsPerRow}` } as CSSProperties}
      >
        {responseMarkups}
      </div>
      {bottomContent}
    </div>
  );
};
